import { Box, Button, ButtonProps, Center, Fade, Flex, Heading } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useResetMeetingMutation } from '@/@generated/codegen/API';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import LiveMeetingInteractionsMenu from '@/components/LiveMeetingInteractionsMenu/LiveMeetingInteractionsMenu';
import MeetingListView, { MeetingListRowType } from '@/components/MeetingList/MeetingListView';
import { ROUTES } from '@/routes/Routes';
import PopConfirm from '@/ui/PopConfirm/PopConfirm';
import { goToLiveMeetingPage } from '@/utils/helpers/goToLiveMeetingPage';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { getMeetingStatus, MEETING_STATUS } from '../../features/meeting/helpers/getMeetingStatus';

// --------------------- styled ------------------------

type Props = {
  meetingList: MeetingListRowType[];
  title?: string;
};

function MeetingListClient({ meetingList, title }: Props) {
  const navigate = useNavigate();
  const { userID, isAdmin } = useLoggedInUserAuthContext();
  const { t } = useTranslation();
  // NOTE: we set the id of a meeting here when we want the action btn to be in a loading state
  const [meetingLoadingID, setMeetingLoadingID] = useState<string | null>(null);

  const [mutationResetMeeting] = useResetMeetingMutation();

  // --------------------- methods ------------------------

  // handle meeting join click
  // eslint-disable-next-line @typescript-eslint/require-await
  const handleMeetingJoin = async (meetingID: string, testMode: boolean = false) => {
    goToLiveMeetingPage({
      meetingID,
      testMode,
    });
  };

  const handleMeetingStats = (meetingID: string) => {
    navigate(`${ROUTES.MEETING_STATS}/${meetingID}`);
  };

  // --------------------- effects ------------------------

  // SIMPLE auto connect
  /*
  NOTE: Auto connect
  Logic:
  1 - From all the meetings being displayed to the user, take all the ones taking place today
  2 - if there is only one and the user is not the host, and is not an admin
  3 - This will make it work even when there are two meetings for example in the list but only 1 is today 
  */
  useEffect(() => {
    if (!meetingList) return;
    const today = new Date().toDateString();

    const meetingsToday = meetingList.filter((meeting) => {
      const eventDate = new Date(meeting.eventDate).toDateString();
      return today === eventDate;
    });
    // we only join if there is ONLY one meeting today
    if (meetingsToday.length === 1) {
      const meetingToJoin = meetingsToday[0];

      if (!isAdmin && meetingToJoin.hostId !== userID) void handleMeetingJoin(meetingToJoin.id);
    }
  }, [meetingList]);

  // --------------------- render ------------------------

  return (
    <Flex w="full">
      <Box p="20px" bg="livelinx.grey100" h="100%" w="full">
        {meetingList.length == 0 && (
          <Fade in={true} delay={1}>
            <Box h="full">
              <Center h="100px">
                <Heading size="sm" color="grey">
                  {t('home.noMeeting')}
                </Heading>
              </Center>
            </Box>
          </Fade>
        )}

        {meetingList.length > 0 && (
          <Fade in={true}>
            <MeetingListView
              data={meetingList}
              showGuestUrl={isAdmin}
              title={title}
              actions={[
                {
                  render: (item) => {
                    const shouldShowStatsBtn =
                      (item.stats?.casePresentationHistory?.length || 0) > 0;
                    const status = getMeetingStatus(item);
                    const isHost = item?.hostId === userID;

                    // main button
                    const mainButtonVisible = [
                      MEETING_STATUS.DEMO,
                      MEETING_STATUS.READY,
                      MEETING_STATUS.TESTING,
                      MEETING_STATUS.STARTED,
                    ].includes(status);

                    const startDemoButton: ButtonProps = {
                      children: t('admin.home.table.startDemo.button'),
                      variant: 'startMeeting',
                      onClick: () => {
                        setMeetingLoadingID(item.id);

                        void mutationResetMeeting({
                          variables: {
                            where: {
                              id: item.id,
                            },
                            data: {
                              setStartDate: false,
                            },
                          },
                        })
                          .then(() => handleMeetingJoin(item.id))
                          .catch((err) => {
                            console.error('Error starting demo meeting from meeting list', err);
                            captureSentry({
                              title: 'Error starting demo meeting from meeting list',
                              error: err,
                            });
                          })
                          .finally(() => {
                            setMeetingLoadingID(null);
                          });
                      },
                    };
                    const joinDemoButton: ButtonProps = {
                      children: t('admin.home.table.joinDemo.button'),
                      variant: 'startMeeting',
                      onClick: () => {
                        void handleMeetingJoin(item.id);
                      },
                    };
                    const startTestingButton: ButtonProps = {
                      children: t('admin.home.table.startTesting.button'),
                      variant: 'primary',
                      onClick: () => {
                        setMeetingLoadingID(item.id);
                        void mutationResetMeeting({
                          variables: {
                            where: {
                              id: item.id,
                            },
                            data: {
                              setStartDate: false,
                            },
                          },
                        })
                          .then(() => {
                            void handleMeetingJoin(item.id, true);
                          })
                          .catch((err) => {
                            console.error('Error starting testing meeting from meeting list', err);
                            captureSentry({
                              title: 'Error starting testing meeting from meeting list',
                              error: err,
                            });
                          })
                          .finally(() => {
                            setMeetingLoadingID(null);
                          });
                      },
                    };
                    const joinTestingButton: ButtonProps = {
                      children: t('admin.home.table.joinTesting.button'),
                      variant: 'primary',
                      onClick: () => {
                        void handleMeetingJoin(item.id);
                      },
                    };

                    const startMeeting: ButtonProps = {
                      children: t('admin.home.table.startMeeting.button'),
                      variant: 'danger',
                      // no on click as we do this in the pop confirm
                      // onClick: async() =>{
                      // }
                    };
                    const joinMeetingButton: ButtonProps = {
                      children: t('admin.home.table.joinMeeting.button'),
                      variant: 'primary',
                      onClick: () => {
                        void handleMeetingJoin(item.id);
                      },
                    };

                    const currentButtonProps = (() => {
                      if (status == MEETING_STATUS.DEMO)
                        return isHost || isAdmin ? startDemoButton : joinDemoButton;
                      if (status == MEETING_STATUS.TESTING)
                        return isHost || isAdmin ? startTestingButton : joinTestingButton;
                      if (status == MEETING_STATUS.READY && isAdmin) return startMeeting;
                      // default
                      return joinMeetingButton;
                    })();

                    const startAfterConfirmation = async () => {
                      await mutationResetMeeting({
                        variables: {
                          where: {
                            id: item.id,
                          },
                          data: {
                            setStartDate: true,
                          },
                        },
                      });
                      await handleMeetingJoin(item.id);
                    };

                    return (
                      <Flex direction="column" gap={2} width="150px">
                        {isAdmin && status === MEETING_STATUS.READY ? (
                          <PopConfirm
                            title="You will now start the Event meeting. Are you sure?" // TODO: translate
                            onConfirm={() => {
                              void startAfterConfirmation();
                            }}
                          >
                            <Button
                              variant="primary"
                              isDisabled={!mainButtonVisible}
                              size="md"
                              w="100%"
                              isLoading={meetingLoadingID === item.id}
                              {...currentButtonProps}
                            />
                          </PopConfirm>
                        ) : (
                          <Button
                            variant="primary"
                            size="md"
                            isDisabled={!mainButtonVisible}
                            w="100%"
                            isLoading={meetingLoadingID === item.id}
                            {...currentButtonProps}
                          />
                        )}

                        {isAdmin && (
                          <>
                            <LiveMeetingInteractionsMenu
                              meeting={item}
                              hostFields={item.hostFields}
                            />
                          </>
                        )}

                        {isHost && (
                          <>
                            {shouldShowStatsBtn && (
                              <Button
                                variant="secondary"
                                size="xs"
                                onClick={() => {
                                  handleMeetingStats(item.id);
                                }}
                              >
                                {t('admin.home.table.meetingStats.button')}
                              </Button>
                            )}
                          </>
                        )}
                      </Flex>
                    );
                  },
                },
              ]}
            />
          </Fade>
        )}
      </Box>
    </Flex>
  );
}

export default MeetingListClient;
